import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { OntologyRecord } from "../../store/useWorkflowStore";
import { cn } from "../../lib/utils";

/**
 * Props for the OntologyRecordsTable component
 */
interface OntologyRecordsTableProps {
  records: OntologyRecord[];
  selectedRecords: Record<string, boolean>;
  onSelectionChange: (recordId: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  /** Source ObjectType ids that have an active Auto-ID config — these rows show a counter toggle. */
  autoIdObjectTypeIds?: string[];
  /** Current per-ObjectType counter choice. */
  counterModeByObjectType?: Record<string, 'NEW' | 'PREVIOUS'>;
  /** Called when the user switches an ObjectType's counter mode. */
  onCounterModeChange?: (objectTypeId: string, mode: 'NEW' | 'PREVIOUS') => void;
}

/**
 * OntologyRecordsTable Component
 * 
 * Displays a table of ontology records with selection capabilities.
 * Records with status "MATCH" cannot be selected.
 */
const OntologyRecordsTable: React.FC<OntologyRecordsTableProps> = ({
  records,
  selectedRecords,
  onSelectionChange,
  onSelectAll,
  autoIdObjectTypeIds = [],
  counterModeByObjectType = {},
  onCounterModeChange
}) => {
  const autoIdSet = new Set(autoIdObjectTypeIds);
  // Get selectable records (status !== "MATCH")
  const selectableRecords = records.filter(record => record.status !== "MATCH");
  const selectableRecordsCount = selectableRecords.length;
  
  // Count of selected records that are also selectable
  const selectedSelectableCount = selectableRecords.filter(record => selectedRecords[record.id]).length;
  
  // Check if all selectable records are selected
  const allSelectableSelected = selectableRecordsCount > 0 && selectedSelectableCount === selectableRecordsCount;
  
  // Handle select all checkbox change
  const handleSelectAllChange = (checked: boolean) => {
    if (onSelectAll) {
      onSelectAll(checked);
    } else {
      // If onSelectAll is not provided, manually toggle all selectable records
      records.forEach(record => {
        if (record.status !== "MATCH") {
          onSelectionChange(record.id, checked);
        }
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all"
            checked={allSelectableSelected}
            onCheckedChange={handleSelectAllChange}
            aria-label="Select all records"
          />
          <label htmlFor="select-all" className="text-sm font-medium">
            Select All
          </label>
        </div>
        <div className="text-sm text-muted-foreground">
          {selectedSelectableCount} of {selectableRecordsCount} selectable records selected
        </div>
      </div>
      
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"></TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead>External ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Object Type</TableHead>
              <TableHead>Auto-ID Counter</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              records.map((record) => {
                const isSelectable = record.status !== "MATCH";
                const isSelected = selectedRecords[record.id] || false;
                
                return (
                  <TableRow 
                    key={record.id}
                    className={cn(
                      !isSelectable && "opacity-60 bg-muted/40",
                      isSelected && "bg-primary/5"
                    )}
                  >
                    <TableCell>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => 
                          onSelectionChange(record.id, checked === true)
                        }
                        disabled={!isSelectable}
                        aria-label={`Select ${record.displayName}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{record.displayName}</TableCell>
                    <TableCell>{record.externalId}</TableCell>
                    <TableCell>{record.type}</TableCell>
                    <TableCell>
                      <Badge
                        variant={record.status === "MISSING" || record.status === "CONFLICT" || record.status === "SYNC_FAILED" ? "destructive" : "outline"}
                        className={cn(
                          record.status === "MATCH" && "bg-green-100 text-green-800 hover:bg-green-100 border-green-200",
                          record.status === "MISSING" && "bg-red-100 text-red-800 hover:bg-red-100 border-red-200",
                          record.status === "CONFLICT" && "bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200",
                          record.status === "SYNC_FAILED" && "bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300"
                        )}
                      >
                        {record.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{record.objectTypeName}</TableCell>
                    <TableCell>
                      {record.type === "OBJECT_TYPE" && autoIdSet.has(record.id) ? (
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={(counterModeByObjectType[record.id] || "NEW") === "NEW" ? "default" : "outline"}
                            onClick={() => onCounterModeChange?.(record.id, "NEW")}
                          >
                            New counter
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={counterModeByObjectType[record.id] === "PREVIOUS" ? "default" : "outline"}
                            onClick={() => onCounterModeChange?.(record.id, "PREVIOUS")}
                          >
                            Previous counter
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OntologyRecordsTable;
